import { Control, useFieldArray } from 'react-hook-form';
import type { input, output } from 'zod';
import { Box, Button, ButtonGroup, Grid, InputAdornment, Tooltip, Typography } from '@mui/material';
import { selectionBonusFormSchema } from '../../schema';
import FormNumberInput from '../molecules/FormNumberInput';
import { Add, Remove } from '@mui/icons-material';

type Props = {
  control: Control<input<typeof selectionBonusFormSchema>, unknown, output<typeof selectionBonusFormSchema>>;
};

const styles = {
  container: {
    maxHeight: 500,
    overflowY: 'auto',
  },
  selectionText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    typography: 'subtitle2',
    fontWeight: 700,
    height: '100%',
  },
  buttonGroup: {
    width: '100%',
  },
  button: {
    borderRadius: 1,
    flex: 1,
  },
  input: {
    textAlign: 'center',
  },
} as const;

export const BonusPerSelectedCombinations = ({ control }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bonusPercentagePerSelection',
  });

  const handleAddClick = () => {
    append({ value: null });
  };

  const handleRemoveClick = () => {
    if (fields.length > 0) {
      remove(fields.length - 1);
    }
  };

  return (
    <Box sx={{ mx: -1 }}>
      <Grid container rowSpacing={1} columnSpacing={1} sx={styles.container}>
        {fields.map((field, index) => (
          <Grid
            key={field.id}
            size={{
              xs: 6,
              md: 4,
              lg: 3,
            }}
          >
            <Grid
              container
              sx={{
                height: '100%',
                alignItems: 'center',
              }}
            >
              <Grid
                sx={{
                  height: '100%',
                }}
                size={4}
              >
                <Typography variant='body2' sx={styles.selectionText}>
                  {index + 1}
                </Typography>
              </Grid>
              <Grid size={8}>
                <FormNumberInput
                  fullWidth
                  control={control}
                  size='small'
                  name={`bonusPercentagePerSelection.${index}.value`}
                  allowDecimals
                  valueAsString
                  showHelperText={false}
                  slotProps={{
                    input: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                    htmlInput: { sx: styles.input },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        ))}
        <Grid
          size={{
            xs: 6,
            md: 4,
            lg: 3,
          }}
        >
          <ButtonGroup size='small' sx={styles.buttonGroup}>
            <Tooltip title='Add selection'>
              <Button color='primary' sx={styles.button} onClick={handleAddClick}>
                <Add />
              </Button>
            </Tooltip>
            <Tooltip title='Remove last selection'>
              <Button color='error' sx={styles.button} onClick={handleRemoveClick} disabled={fields.length === 0}>
                <Remove />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Box>
  );
};
